import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {
  TrustSafetyContent,
  TrustSafetyTopic,
  trustSafetyService,
} from '../services/trustSafetyService';

// FEAT-061 — Caregiver Trust & Safety onboarding step (web).
// MVP-8.20, MVP-8.21, MVP-8.22, MVP-8.23, MVP-8.24, MVP-8.25, MVP-8.26,
// MVP-8.27 — the eight §8 trust-and-safety basics. The step presents all
// eight topics and requires the caregiver to explicitly acknowledge every
// one before continuing; on submit the FULL topic-key set is POSTed with
// the current content version. If the caregiver has already acknowledged
// the current version, the step short-circuits to a warm "all set" state.
//
// Tone (§1.2 elderly-friendly + warm caregiver audience): calm, supportive
// copy, generous spacing, large readable type — never red error styling
// for routine guidance.

const HEADER_TITLE = 'A few basics before you start caring';
const HEADER_BODY =
  "These short reminders help keep everyone safe and cared for. Please read each one and check the box to show you understand. We'll only ask again if anything important changes.";
const ACKNOWLEDGE_ALL_LABEL =
  'I have read and understand all of the trust & safety basics above.';
const COMPLETE_TITLE = "You're all set with trust & safety";
const COMPLETE_BODY =
  "Thank you for taking the time. You've acknowledged the current trust & safety basics — we'll only ask again if they change.";

interface CaregiverTrustSafetyStepProps {
  /** Called once the caregiver has acknowledged (or already had acknowledged) the current version. */
  onComplete?: () => void;
}

const CaregiverTrustSafetyStep: React.FC<CaregiverTrustSafetyStepProps> = ({
  onComplete,
}) => {
  const [content, setContent] = useState<TrustSafetyContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadyAcknowledged, setAlreadyAcknowledged] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [contentResult, status] = await Promise.all([
          trustSafetyService.getTrustSafetyContent(),
          trustSafetyService.getAcknowledgement().catch(() => null),
        ]);
        if (cancelled) return;
        setContent(contentResult);
        // Skip/short-circuit when the caregiver has already acknowledged the
        // current content version.
        if (
          status &&
          status.acknowledged &&
          status.contentVersion === contentResult.contentVersion
        ) {
          setAlreadyAcknowledged(true);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'We had trouble loading the trust & safety basics. Please try again.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const topics: TrustSafetyTopic[] = useMemo(() => content?.topics ?? [], [content]);

  const allChecked = useMemo(
    () => topics.length > 0 && topics.every((t) => checkedKeys[t.key]),
    [topics, checkedKeys],
  );

  const toggleTopic = useCallback((key: string, checked: boolean) => {
    setCheckedKeys((prev) => ({ ...prev, [key]: checked }));
  }, []);

  const toggleAll = useCallback(
    (checked: boolean) => {
      const next: Record<string, boolean> = {};
      topics.forEach((t) => {
        next[t.key] = checked;
      });
      setCheckedKeys(next);
    },
    [topics],
  );

  const handleSubmit = useCallback(async () => {
    if (!content || !allChecked) return;
    setSubmitting(true);
    setError(null);
    try {
      // Send the FULL topic-key set for the current version — the API rejects
      // a partial set with 400 INCOMPLETE_ACKNOWLEDGEMENT.
      await trustSafetyService.acknowledge(
        content.contentVersion,
        topics.map((t) => t.key),
      );
      setAlreadyAcknowledged(true);
      onComplete?.();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "We couldn't record your acknowledgement just now. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }, [content, allChecked, topics, onComplete]);

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', py: 8 }}
        data-testid="trust-safety-loading"
      >
        <CircularProgress aria-label="Loading trust & safety basics" />
      </Box>
    );
  }

  if (error && !content) {
    return (
      <Alert severity="info" data-testid="trust-safety-error">
        {error}
      </Alert>
    );
  }

  if (alreadyAcknowledged) {
    return (
      <Card
        data-testid="trust-safety-complete"
        sx={{ borderLeft: '4px solid', borderColor: 'success.main' }}
      >
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
            <CheckCircleIcon color="success" aria-hidden="true" />
            <Typography variant="h5" component="h2">
              {COMPLETE_TITLE}
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {COMPLETE_BODY}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={onComplete}
            data-testid="trust-safety-continue"
          >
            Continue
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box data-testid="trust-safety-step">
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <ShieldOutlinedIcon color="primary" aria-hidden="true" />
            <Typography variant="h4" component="h1">
              {HEADER_TITLE}
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.secondary">
            {HEADER_BODY}
          </Typography>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="trust-safety-error">
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        {topics.map((topic, index) => (
          <Card
            key={topic.key}
            data-testid={`trust-safety-topic-${topic.key}`}
            data-mvp-ref={topic.mvpRef}
          >
            <CardContent>
              <Typography
                variant="overline"
                color="primary"
                sx={{ display: 'block' }}
              >
                Basic {index + 1} of {topics.length}
              </Typography>
              <Typography variant="h6" component="h2" gutterBottom>
                {topic.title}
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {topic.summary}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {topic.body}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!checkedKeys[topic.key]}
                    onChange={(e) => toggleTopic(topic.key, e.target.checked)}
                    inputProps={
                      {
                        'aria-label': `I understand: ${topic.title}`,
                        'data-testid': `trust-safety-topic-checkbox-${topic.key}`,
                      } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                }
                label={`I understand "${topic.title}"`}
              />
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={allChecked}
                indeterminate={
                  !allChecked && topics.some((t) => checkedKeys[t.key])
                }
                onChange={(e) => toggleAll(e.target.checked)}
                inputProps={
                  {
                    'aria-label': ACKNOWLEDGE_ALL_LABEL,
                    'data-testid': 'trust-safety-acknowledge-all',
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
            label={ACKNOWLEDGE_ALL_LABEL}
          />
          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={submitting || !allChecked}
              onClick={handleSubmit}
              data-testid="trust-safety-continue"
            >
              {submitting ? 'Saving…' : 'I acknowledge — continue'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CaregiverTrustSafetyStep;
